import { initializeApp } from 'firebase/app';
import logger from '../utils/logger';
import { 
  getAuth, 
  signInWithPopup, 
  OAuthProvider, 
  onAuthStateChanged, 
  onIdTokenChanged, 
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
const provider = new OAuthProvider('microsoft.com');

// Local storage key for email link sign-in
const EMAIL_FOR_SIGNIN_KEY = 'emailForSignIn';
const POST_SIGNIN_REDIRECT_KEY = 'postSignInRedirectPath';

export async function loginWithMicrosoft() {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    logger.debug("User info:", user);
    const idToken = await result.user.getIdToken();
    return { user: result.user, idToken };
  } catch (error) {
    logger.error("Error logging in with Microsoft:", error);
    alert("Microsoft sign-in failed. Please try again or contact admin." + (error as Error).message);
    throw error;
  }
}

/**
 * Send a sign-in link to the specified email address (passwordless authentication).
 * Used for Guest/Partner access where users may not have Microsoft accounts.
 */
export async function sendEmailSignInLink(email: string): Promise<void> {
  const redirectPath = getPostSignInRedirectPath()
    || `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const actionCodeSettings = {
    // URL where user will be redirected after clicking the link
    url: `${window.location.origin}/auth/email-link-callback?redirect=${encodeURIComponent(redirectPath)}`,
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email locally to complete sign-in when user returns
    window.localStorage.setItem(EMAIL_FOR_SIGNIN_KEY, email);
    logger.info(`Sign-in link sent to: ${email}`);
  } catch (error) {
    logger.error("Error sending email sign-in link:", error);
    throw error;
  }
}

/**
 * Check if the current URL is a sign-in with email link callback.
 */
export function isEmailLinkSignIn(): boolean {
  return isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Complete the email link sign-in process.
 * Called when user clicks the magic link and returns to the app.
 */
export async function completeEmailLinkSignIn(email?: string): Promise<{ user: any; idToken: string }> {
  // Get email from localStorage if not provided
  const signInEmail = email || window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
  
  if (!signInEmail) {
    throw new Error('Email is required to complete sign-in. Please enter your email address.');
  }

  try {
    const result = await signInWithEmailLink(auth, signInEmail, window.location.href);
    // Clear the email from localStorage
    window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
    
    const idToken = await result.user.getIdToken();
    logger.info(`Email link sign-in completed for: ${signInEmail}`);
    return { user: result.user, idToken };
  } catch (error: any) {
    logger.error("Error completing email link sign-in:", error);
    
    // Provide user-friendly error messages
    const errorCode = error?.code || '';
    if (errorCode === 'auth/invalid-action-code') {
      throw new Error('This sign-in link has expired or has already been used. Please request a new link.');
    } else if (errorCode === 'auth/invalid-email') {
      throw new Error('The email address you entered does not match the one used to request this link.');
    } else if (errorCode === 'auth/user-disabled') {
      throw new Error('This account has been disabled. Please contact admin.');
    } else if (errorCode === 'auth/expired-action-code') {
      throw new Error('This sign-in link has expired. Please request a new link.');
    }
    throw error;
  }
}

/**
 * Get the email stored for email link sign-in (if any).
 */
export function getStoredEmailForSignIn(): string | null {
  return window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
}

/**
 * Clear the stored email for sign-in.
 */
export function clearStoredEmailForSignIn(): void {
  window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
}

export function setPostSignInRedirectPath(path: string): void {
  if (!path) {
    return;
  }
  window.localStorage.setItem(POST_SIGNIN_REDIRECT_KEY, path);
}

export function getPostSignInRedirectPath(): string | null {
  return window.localStorage.getItem(POST_SIGNIN_REDIRECT_KEY);
}

export function clearPostSignInRedirectPath(): void {
  window.localStorage.removeItem(POST_SIGNIN_REDIRECT_KEY);
}

export function watchAuth(callback: (user: any | null) => void) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Watch for token changes (fires on login, logout, AND automatic token refresh).
 * Use this instead of watchAuth when you need to keep tokens in sync.
 */
export function watchIdToken(callback: (user: any | null) => void) {
  return onIdTokenChanged(auth, callback);
}

export async function logout() {
  await signOut(auth);
}
